import { v4 as uuidv4 } from 'uuid';
import { Item, Bin, Packer } from '../../../../../../utils/bin-packing-v6/3D';

export function BoxUtil(props) {
    return {
        boxUp
    }
}

async function boxUp(purchasingItems, allowedPalletWidth, allowedPalletHeight, allowedPalletDepth, maxCBMOfExtraItems, standardRules = []) {
    const normalItems = [];
    const extraItems = [];
    const heteromorphicItems = [];
    let quantityZeroItems = [];

    const pallets = [];

    // 규격 규칙에 매칭되는 상품은 빈패킹 알고리즘/박스 크기를 무시하고 고정 개수로 먼저 적재한다.
    const rules = normalizeStandardRules(standardRules);
    const { groups: standardGroups, rest: restItems } = partitionByStandardRules(purchasingItems, rules);
    buildStandardPallets(standardGroups, pallets);

    // 구매상품을 박스로 나누기 (규격 매칭 제외분만)
    const dividedItems = restItems?.reduce((prev, curr) => {
        if (!curr.boxQuantity || curr.boxQuantity <= 0) {
            return prev.concat({ ...curr, devidedSubItemUUID: uuidv4(), boxQuantity: 0, estimatedProductQuantity: curr?.productQuantity })
        }

        const items = [];
        const estimatedProductQuantity = Math.round((curr?.productQuantity / curr.boxQuantity) * 100) / 100;

        for (let i = 0; i < curr.boxQuantity; i++) {
            items.push({
                ...curr,
                devidedSubItemUUID: uuidv4(),
                boxQuantity: 1,
                estimatedProductQuantity: estimatedProductQuantity,
            })
        }

        return prev.concat([...items])
    }, []);

    // 구매상품을 상품명과 옵션명으로 정렬
    dividedItems.sort((a, b) => {
        if (`${a.warehousingProductName}_${a.warehousingOptionName}` < `${b.warehousingProductName}_${b.warehousingOptionName}`) return -1;
        if (`${a.warehousingProductName}_${a.warehousingOptionName}` > `${b.warehousingProductName}_${b.warehousingOptionName}`) return 1;
        return 0;
    });

    // 분할된 상품을 박스의 크기별로 해당하는 필드로 매핑
    dividedItems?.forEach(r => {
        const boxSize = [r?.boxWidth, r?.boxDepth, r?.boxHeight]; // mm 단위로 변환

        boxSize.sort((a, b) => b - a); // 가장 긴 부분을 width, 두번째로 긴 부분은 depth, 가장 짧은 부분을 height로 정렬

        if (!r?.boxQuantity || r?.boxQuantity <= 0) {
            quantityZeroItems.push({
                ...r,
                boxWidth: boxSize[0],
                boxDepth: boxSize[1],
                boxHeight: boxSize[2],
            });
            return;
        };

        // 박스의 사이즈가 허용된 팔렛트의 사이즈보다 크다면
        if ((boxSize[0] > allowedPalletWidth && boxSize[0] > allowedPalletDepth) || (boxSize[1] > allowedPalletWidth && boxSize[1] > allowedPalletDepth)) {
            heteromorphicItems.push({
                ...r,
                boxWidth: boxSize[0],
                boxDepth: boxSize[1],
                boxHeight: boxSize[2],
            })
            return;
        }

        normalItems.push({
            ...r,
            boxWidth: boxSize[0],
            boxDepth: boxSize[1],
            boxHeight: boxSize[2],
        });
    });

    // 같은 상품명과 옵션명을 가진 상품들을 묶어서 CBM이 maxCBMOfExtraItems보다 작은 경우 extraItems로 이동
    let normalItemsMap = new Map();
    normalItems?.forEach(devidedItem => {
        const key = devidedItem.warehousingProductName;

        if (normalItemsMap.has(key)) {
            const value = normalItemsMap.get(key);
            value.push(devidedItem);
            normalItemsMap.set(key, value);
        } else {
            normalItemsMap.set(key, [devidedItem]);
        }
    })

    for (let [key, value] of normalItemsMap) {
        const totalCBM = value.reduce((acc, item) => {
            return acc + item.boxCBM;
        }, 0);

        if (totalCBM <= maxCBMOfExtraItems) {
            extraItems.push(...value);
            normalItemsMap.delete(key);
        }
    }

    // normalItem 파레트 적재
    for (let [key, value] of normalItemsMap) {
        const items = value;
        const packToPalletResult = packToPallet(items, pallets, 'NORMAL', allowedPalletWidth, allowedPalletDepth, allowedPalletHeight, maxCBMOfExtraItems);
        pallets.push(...packToPalletResult);
    }

    // extraItem 파레트 적재    
    const extraItemPackToPalletResult = packToPallet(extraItems, pallets, 'EXTRA', allowedPalletWidth, allowedPalletDepth, allowedPalletHeight, maxCBMOfExtraItems);
    pallets.push(...extraItemPackToPalletResult);

    const heteromorphicItemPackToPalletResult = packToPallet(heteromorphicItems, pallets, 'HETEROMORPHIC', allowedPalletWidth + 500, allowedPalletDepth + 500, allowedPalletHeight, maxCBMOfExtraItems);
    pallets.push(...heteromorphicItemPackToPalletResult);

    pallets?.forEach((pallet, idx) => {
        // 규격 파레트는 매칭 상품만 담아야 하므로 잔여 수량0 상품을 섞지 않는다.
        if (pallet.type === 'STANDARD') {
            return;
        }
        quantityZeroItems.forEach(quantityZeroItem => {
            const matchingItem = pallet.devidedSubItems.find(devidedSubItem => devidedSubItem.warehousingProductName === quantityZeroItem.warehousingProductName);
            if (matchingItem) {
                pallet.devidedSubItems.push(quantityZeroItem);
                quantityZeroItems = quantityZeroItems.filter(item => item !== quantityZeroItem);
            }
        });
    })

    return pallets
}

function packToPallet(items, originPallet, type = 'NORMAL', palletWidth, palletDepth, palletHeight, maxCBMOfExtraItems) {
    let targetItems = items.map(r => {
        return {
            ...r,
        }
    })
    let pallets = [];
    let palletIdx = 1;
    let trierCount = 0;


    while (targetItems?.length > 0) {
        let packer = new Packer();
        // let pallet = new Bin("PALLET", palletWidth, palletHeight, palletDepth);
        let pallet = new Bin("PALLET", palletWidth, palletHeight, palletDepth, 10000, 100);

        packer.addBin(pallet);

        targetItems.forEach(r => {
            const itemWidth = r.boxQuantity <= 0 ? 0 : r.boxWidth;
            const itemDepth = r.boxQuantity <= 0 ? 0 : r.boxDepth;
            const itemHeight = r.boxQuantity <= 0 ? 0 : r.boxHeight;
            const mustBeFlat = shouldItemBeFlat(itemWidth, itemHeight, itemDepth);
            packer.addItem(new Item(`${r.devidedSubItemUUID}`, itemWidth, itemHeight, itemDepth, null, null, mustBeFlat));
        })

        // pack items into bin1
        packer.pack();
        let palletedIds = pallet.items?.map(r => r.name);
        let unfitedItemIds = packer.unfitItems?.map(r => r.name);

        const palletedItems = targetItems.filter(r => palletedIds.includes(r.devidedSubItemUUID)).map(r => {
            let newItem = { ...r };
            return newItem
        });

        if (palletedItems?.length > 0) {
            pallets = pallets.concat({
                type: type,
                palletUUID: uuidv4(),
                palletNumber: originPallet.length + palletIdx,
                devidedSubItems: palletedItems,
            })
        }

        if (unfitedItemIds?.length <= 0) {
            break;
        }

        targetItems = targetItems.filter(r => unfitedItemIds.includes(r.devidedSubItemUUID));
        palletIdx++;
        trierCount++;

        if (trierCount > items?.length) {
            console.log('trierCount', trierCount)
            if (targetItems?.length > 0) {
                pallets = pallets.concat({
                    type: type,
                    palletUUID: uuidv4(),
                    palletNumber: originPallet.length + palletIdx - trierCount,
                    devidedSubItems: targetItems,
                })
            }
            break;
        }

        // // item1, item2, item3
        // console.log(palletedIds);

        // // items will be empty, all items was packed
        // console.log(packer.items);

        // // unfitItems will be empty, all items fit into bin1
        // console.log(packer.unfitItems)
    }

    return pallets;
}

function shouldItemBeFlat(W, H, D, stableHeightThreshold = 200) {
    // 1. 절대 기준 활용
    if (H < stableHeightThreshold) {
        return true;
    }

    // 2. 상대 기준 활용
    // 예: 높이가 다른 치수에 비해 극단적으로 낮으면 평평하게 놓아야 한다고 판단
    const smallestHorizontalDim = Math.min(W, D);
    if (H < 0.5 * smallestHorizontalDim) {
        return true;
    }

    // 위의 기준에 해당하지 않으면 세울 수도 있다는 가정 하에 false 반환
    return false;
}

// ─────────────────────────────────────────────────────────────
// 규격 적재 규칙 (standard rules)
// 특정 상품(군)을 알고리즘/박스 크기 무시하고 한 파레트에 고정 개수로 적재
// ─────────────────────────────────────────────────────────────

/** 유효한 규칙만 남긴다 (박스수 > 0, 조건 1개 이상). */
function normalizeStandardRules(standardRules) {
    return (standardRules || []).filter(rule =>
        rule &&
        Number(rule.boxesPerPallet) > 0 &&
        Array.isArray(rule.conditions) &&
        rule.conditions.length > 0
    );
}

/** 상품이 규칙의 조건 중 하나라도 매칭되는가 (옵션코드 일치). */
function matchesStandardRule(rule, item) {
    const itemOptionCode = (item?.optionCode || '').trim();
    if (!itemOptionCode) {
        return false;
    }

    return (rule.conditions || []).some(condition => {
        const optionCode = (condition?.productOptionCode || '').trim();
        return !!optionCode && optionCode === itemOptionCode;
    });
}

/** 구매상품을 규칙 매칭 그룹과 나머지로 분리한다. 한 상품은 먼저 매칭된 규칙에만 속한다. */
function partitionByStandardRules(purchasingItems, rules) {
    const groupMap = new Map(); // ruleIndex -> { rule, items }
    const rest = [];

    (purchasingItems || []).forEach(item => {
        const ruleIndex = rules.findIndex(rule => matchesStandardRule(rule, item));
        if (ruleIndex === -1) {
            rest.push(item);
            return;
        }
        if (!groupMap.has(ruleIndex)) {
            groupMap.set(ruleIndex, { rule: rules[ruleIndex], items: [] });
        }
        groupMap.get(ruleIndex).items.push(item);
    });

    return { groups: Array.from(groupMap.values()), rest };
}

/** 매칭 그룹을 boxesPerPallet 개씩 묶어 STANDARD 파레트로 만들어 pallets 배열에 추가한다. */
function buildStandardPallets(groups, pallets) {
    groups.forEach(group => {
        const boxesPerPallet = Math.max(1, Math.floor(Number(group.rule.boxesPerPallet) || 1));
        const ruleName = group.rule.name || '';

        // 박스 단위로 분해 (수량0 상품은 별도로 모아 마지막에 부착)
        const unitBoxes = [];
        const zeroItems = [];
        group.items.forEach(curr => {
            const boxSize = [curr?.boxWidth, curr?.boxDepth, curr?.boxHeight].sort((a, b) => b - a);
            const base = { ...curr, boxWidth: boxSize[0], boxDepth: boxSize[1], boxHeight: boxSize[2] };

            if (!curr.boxQuantity || curr.boxQuantity <= 0) {
                zeroItems.push({ ...base, devidedSubItemUUID: uuidv4(), boxQuantity: 0, estimatedProductQuantity: curr?.productQuantity });
                return;
            }

            const estimatedProductQuantity = Math.round((curr?.productQuantity / curr.boxQuantity) * 100) / 100;
            for (let i = 0; i < curr.boxQuantity; i++) {
                unitBoxes.push({ ...base, devidedSubItemUUID: uuidv4(), boxQuantity: 1, estimatedProductQuantity: estimatedProductQuantity });
            }
        });

        const groupPallets = [];
        for (let i = 0; i < unitBoxes.length; i += boxesPerPallet) {
            groupPallets.push({
                type: 'STANDARD',
                standardRuleName: ruleName,
                palletUUID: uuidv4(),
                palletNumber: pallets.length + groupPallets.length + 1,
                devidedSubItems: unitBoxes.slice(i, i + boxesPerPallet),
            });
        }

        // 수량0 매칭 상품은 그룹 첫 파레트에 부착 (없으면 빈 규격 파레트 하나 생성)
        if (zeroItems.length > 0) {
            if (groupPallets.length === 0) {
                groupPallets.push({
                    type: 'STANDARD',
                    standardRuleName: ruleName,
                    palletUUID: uuidv4(),
                    palletNumber: pallets.length + 1,
                    devidedSubItems: [],
                });
            }
            groupPallets[0].devidedSubItems.push(...zeroItems);
        }

        pallets.push(...groupPallets);
    });
}